import {queryOptions} from '@tanstack/react-query';

import {apiClient} from 'common/util/api-client';

import {appApiUrl} from '../utils';

export type SimilarEvent = {
  /** Raw event identifier as used in queries (`$custom_event:N` for custom events) */
  name: string;
  /** 1-based position, closest match first */
  rank: number;
  /** Cosine distance between the query and the event, smaller is closer */
  distance?: number;
};

export type SimilarEventsQuery = {
  projectId: string;
  /** Scopes the search to a data view; the search runs as the project's global view without one */
  workspaceId?: string;
  name: string;
  description?: string;
  topK?: number;
};

export const SIMILAR_EVENTS_STALE_TIME_MS = 1000 * 60 * 5;

/**
 * Events from the project's Copilot index that are closest to an event-shaped query, served by
 * GET /api/app/{projects,workspaces}/{id}/data-definitions/events/similar.
 * Rejects when the request fails, so a failure is not cached as "no matches".
 */
export async function fetchSimilarEvents({
  projectId,
  workspaceId,
  name,
  description,
  topK,
}: SimilarEventsQuery): Promise<SimilarEvent[]> {
  const params: Record<string, string | number> = {name};
  if (description) {
    params.description = description;
  }
  if (topK) {
    params.top_k = topK;
  }

  const results = await apiClient.get<SimilarEvent[]>(
    appApiUrl(`data-definitions/events/similar`, {projectId, workspaceId}),
    params,
  );
  return Array.isArray(results) ? results : [];
}

export function similarEventsQueryOptions(query: SimilarEventsQuery) {
  const {projectId, workspaceId, name, description, topK} = query;
  return queryOptions({
    queryKey: [`similarEvents`, projectId, workspaceId ?? ``, name, description ?? ``, topK ?? null] as const,
    queryFn: () => fetchSimilarEvents(query),
    staleTime: SIMILAR_EVENTS_STALE_TIME_MS,
  });
}
